import request from './request';

const API = 'https://server.battleverse.cn/fight';
const jobs = new Map();
const listeners = new Set();
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const owner = () => {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return String(u.id || u.userId || ''); }
  catch { return ''; }
};
const publish = () => listeners.forEach(fn => fn([...jobs.values()].filter(j => j.record.owner === owner()).map(j => ({ ...j.view, requestId: j.record.requestId }))));
let database;
async function db() {
  if (!database) database = new Promise((resolve, reject) => {
    const open = indexedDB.open('pb-fight-queue', 1);
    open.onupgradeneeded = () => open.result.createObjectStore('jobs', { keyPath: 'requestId' });
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
  return database;
}
async function stored(action, record) {
  const database = await db();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('jobs', action === 'getAll' ? 'readonly' : 'readwrite');
    const store = tx.objectStore('jobs');
    const operation = action === 'getAll' ? store.getAll() : action === 'put' ? store.put(record) : store.delete(record);
    tx.oncomplete = () => resolve(operation.result);
    tx.onerror = () => reject(tx.error);
  });
}
async function persist(record) { try { await stored('put', record); } catch { /* Current-page execution remains available. */ } }
const post = (path, body) => request.post(`${API}/queue/${path}.do`, body, { timeout: 12000 });
const update = (job, value) => { job.view = { ...job.view, ...value }; publish(); };

async function run(job) {
  const r = job.record;
  job.transfer = null;
  for (;;) {
    if (owner() !== r.owner || !localStorage.getItem('token')) throw Object.assign(new Error('请登录原账号查看提交结果'), { resumeOnLogin: true });
    try {
      const firstAdmission = !r.ticketId;
      const reply = r.ticketId
        ? await post('status', { ticketId: r.ticketId })
        : await post('join', { kind: r.kind, fightId: r.fightId, requestId: r.requestId, creationId: r.creationId, status: r.status });
      const state = reply.data;
      if (!r.ticketId) { r.ticketId = state.ticketId; await persist(r); }
      update(job, { ...state, reconnecting: false });
      if (state.state === 'DONE') {
        try { await stored('delete', r.requestId); } catch {}
        return state.result || { code: 0, success: true, msg: state.message };
      }
      if (state.state === 'FAILED' || state.state === 'CANCELLED') {
        try { await stored('delete', r.requestId); } catch {}
        throw Object.assign(new Error(state.message || '提交未完成'), { terminal: true, queueState: state.state, cancelled: state.state === 'CANCELLED' });
      }
      if (r.kind === 'upload' && state.state === 'READY' && !job.transfer) {
        if (!r.file) throw Object.assign(new Error('无法恢复图片，请联系现场管理员'), { terminal: true });
        const form = new FormData();
        form.append('upfile', r.file, r.file.name || 'creation.png');
        form.append('ticketId', r.ticketId);
        update(job, { progress: 0, sending: true });
        // Poll separately while bytes are sent. A timeout is not treated as a confirmed failure.
        job.transfer = request.post(`${API}/upload.do`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000,
          onUploadProgress: e => update(job, { progress: e.total ? Math.round(e.loaded * 100 / e.total) : null, sending: true })
        }).catch(() => {}).finally(() => { job.transfer = null; update(job, { sending: false }); });
      }
      if (state.state === 'RETRY' && !job.transfer) {
        await post('retry', { ticketId: r.ticketId });
      }
      if (firstAdmission && r.kind === 'vote') continue;
    } catch (error) {
      if (error.terminal || error.code === -1 || error.code === -2) throw error;
      update(job, { message: '连接暂时中断，正在确认提交结果，请保持页面打开', reconnecting: true });
      await sleep(2000);
    }
    await sleep(1200 + Math.random() * 800);
  }
}
function start(record) {
  const previous = jobs.get(record.requestId);
  if (previous && !previous.resumeOnLogin) return previous.promise;
  const job = { record, view: { kind: record.kind, state: 'CONNECTING', message: '正在确认排队资格' } };
  jobs.set(record.requestId, job); publish();
  job.promise = run(job).then(result => {
    update(job, { state: 'DONE', message: record.kind === 'upload' ? '上传成功' : '投票已提交', reconnecting: false });
    window.dispatchEvent(new CustomEvent('pb-queue-completed', { detail: { kind: record.kind, fightId: record.fightId } }));
    return result;
  }).catch(async error => {
    if (error.code === -1 || error.terminal) { try { await stored('delete', record.requestId); } catch {} }
    job.resumeOnLogin = error.resumeOnLogin || error.code === -2;
    update(job, { state: error.queueState || 'FAILED', message: error.message, reconnecting: false });
    throw error;
  });
  return job.promise;
}
export async function queueUpload(file, fightId) {
  if (!file || file.size > 20 * 1024 * 1024) throw new Error('请选择不超过 20 MB 的图片');
  if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name || '')) throw new Error('请选择 PNG、JPG、WEBP 或 GIF 图片');
  const record = { requestId: crypto.randomUUID(), owner: owner(), kind: 'upload', fightId, file };
  await persist(record);
  return start(record);
}
export async function queueVote(input) {
  const record = { ...input, requestId: crypto.randomUUID(), owner: owner(), kind: 'vote' };
  await persist(record);
  return start(record);
}
export function subscribeQueue(listener) { listeners.add(listener); publish(); return () => listeners.delete(listener); }
export async function restoreQueue() {
  publish();
  if (!owner() || !localStorage.getItem('token')) return;
  let saved;
  try { saved = await stored('getAll'); } catch { return; }
  for (const r of saved) if (r.owner === owner()) start(r).catch(() => {});
}
export async function cancelQueue(requestId) {
  const job = jobs.get(requestId);
  if (!job?.record.ticketId) return;
  try {
    const reply = await post('cancel', { ticketId: job.record.ticketId });
    if (reply.data.state === 'CANCELLED') {
      try { await stored('delete', requestId); } catch {}
    }
    update(job, reply.data);
  }
  catch (e) { update(job, { message: e.message }); }
}
export async function retryQueue(requestId) {
  const job = jobs.get(requestId);
  if (!job?.record.ticketId) return;
  try { const reply = await post('retry', { ticketId: job.record.ticketId }); update(job, reply.data); }
  catch (e) { update(job, { message: e.message }); }
}
export function dismissQueue(requestId) { jobs.delete(requestId); publish(); }
export const queueOverview = () => post('overview', {});
